'use client';

import {
  CoEditRoleCGType,
  DestinationCGType,
  DestinationInputCGType,
  SearchResultCGType,
  SurveyCGType,
} from '@/types/graphql/root';
import {
  filterNullUndefined,
  getFormattedMainCity,
  getSortedDestinationList,
  mapSlotTypeToPoiType,
  PoiType,
  redirectToZzimSignin,
} from '@/utils';
import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation';
import { addDestinations, getTrip, replaceSlot, searchPoisUsingCoords } from '@/utils/actions';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import TripTopBar from './trip-top-bar';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import isEqual from 'lodash/isEqual';
import {
  globalTripAtom,
  globalTripDestinationsAtom,
  globalTripIdAtom,
  isEditRoleAtom,
  topDestSlotAtom,
  tripPoiModalAtom,
} from '@/atom/trip';
import { historyStackAtom } from '@/atom/historyStack';
import { history } from '@/utils/history';
import { confirmModalAtom, tripShareModalAtom } from '@/atom/modal';
import SelectDayModal from './select-day-modal/select-day-modal';
import ModifySurveyDateModal from './select-day-modal/modify-survey-date-modal';
import SelectDayForAddDestModal from './select-day-for-add-dest-modal';
import PoiModalInTrip from './poi-modal-in-trip';
import ZzimcarProductsList from './zzimcar-products-list';
import EmbeddedOverlay from './embedded-overlay';
import AllThatTravelHintModal from '../allthattravel/allthattravel-hint-modal';
import HandCursor2734 from '@svg/allthattravel/hand_cursor_27_34.svg';
import TripMapView from './client-trip/trip-map-view';
import TripSearchBar from './client-trip/trip-search-bar';
import TripItineraryView from './client-trip/trip-itinerary-view';
import TripSearchView from '@/app/ui/trip/client-trip/trip-search-view';
import TripBottomBar from '@/app/ui/trip/client-trip/trip-bottom-bar';
import TripGuestLoginPrompt from '@/app/ui/trip/client-trip/trip-guest-login-prompt';
import TripHeader from '@/app/ui/trip/client-trip/trip-header';
import SelectDayForMoveDestModal from './select-day-for-move-dest-modal';
import useMap from '@/app/hooks/useMap';
import useTripSocket from '@/app/hooks/useTripSocket';
import useAutofillTrip from '@/app/hooks/useAutofillTrip';
import useCopyTrip from '@/app/hooks/useCopyTrip';
import useGetAllSavedPoisQuery from '@/app/hooks/useGetAllSavedPoisQuery';
import useTripData from '@/app/hooks/trip/useTripData';
import useTripItineraryInteractions from '@/app/hooks/trip/useTripItineraryIntercations';
import useGuestBlockBackward from '@/app/hooks/trip/useGuestBlockBackward';
import useItineraryHeight from '@/app/hooks/trip/useItineraryHeight';
import useItineraryResize from '@/app/hooks/trip/useItineraryResize';
import useTripDates from '@/app/hooks/trip/useTripDates';
import useDragAndDrop from '@/app/hooks/trip/useDragAndDrop';
import useTripScroll from '@/app/hooks/trip/useTripScroll';
import useActionHandler from '@/app/hooks/useActionHandler';

export const TRIP_MODE = {
  VIEW: 'VIEW',
  SEARCH: 'SEARCH',
  EDIT: 'EDIT',
  MEMO: 'MEMO',
} as const;

export type TripModeType = (typeof TRIP_MODE)[keyof typeof TRIP_MODE];

export const TRIP_CONDITIONS = {
  IS_VIEW_MODE: (mode: TripModeType) => mode === TRIP_MODE.VIEW,
  IS_SEARCH_MODE: (mode: TripModeType) => mode === TRIP_MODE.SEARCH,
  IS_EDIT_MODE: (mode: TripModeType) => mode === TRIP_MODE.EDIT,
  IS_MEMO_MODE: (mode: TripModeType) => mode === TRIP_MODE.MEMO,
  IS_VIEW_OR_MEMO_MODE: (mode: TripModeType) => mode === TRIP_MODE.VIEW || mode === TRIP_MODE.MEMO,
  IS_NOT_SEARCH_MODE: (mode: TripModeType) => mode !== TRIP_MODE.SEARCH,
  CAN_EDIT: (isEditRole: boolean, mode: TripModeType) =>
    isEditRole && (mode === TRIP_MODE.EDIT || mode === TRIP_MODE.MEMO || mode === TRIP_MODE.SEARCH),
  SHOULD_SHOW_LOGIN_PROMPT: (mode: TripModeType, isOwn: boolean, isLoggedin: boolean) =>
    mode === TRIP_MODE.VIEW && isOwn && !isLoggedin,
} as const;

export const ITINERARY_HEIGHT_MODE = {
  FULL: 'FULL',
  DEFAULT: 'DEFAULT',
  MINIMALIZE: 'MINIMALIZE',
} as const;

export type ItineraryHeightModeType = (typeof ITINERARY_HEIGHT_MODE)[keyof typeof ITINERARY_HEIGHT_MODE];
export const ITINERARY_HEIGHT_MODE_CONDITIONS = {
  IS_FULL: (mode: ItineraryHeightModeType) => mode === ITINERARY_HEIGHT_MODE.FULL,
  IS_DEFAULT: (mode: ItineraryHeightModeType) => mode === ITINERARY_HEIGHT_MODE.DEFAULT,
  IS_MINIMALIZE: (mode: ItineraryHeightModeType) => mode === ITINERARY_HEIGHT_MODE.MINIMALIZE,
} as const;

type DestinationPosition = {
  day: number;
  pos: number;
  replace?: boolean;
  replaceDestId?: string;
};
export interface ClientTripPageProps {
  initIsLoggedin: boolean;
  initTrip: SurveyCGType | null;
  initMemoDictionary: { [key: string]: string };
  initTopSlot: DestinationCGType | null;
  initZzimcarProductsProps: {
    zzimRentCarZone: string | null;
    zzimRentCarZoneName: string | null;
    startDate: any;
    endDate: any;
  };
  isEmbedded: boolean;
  initSelectedPoiId?: string | null;
}

type DestinationModeType = 'selectDay' | 'normal';
type LastSearchCenterAndZoomType = {
  lat: number;
  lng: number;
  zoom: number;
} | null;

const useSearchState = () => {
  const [tempSearchResult, setTempSearchResult] = useState<SearchResultCGType | null>(null);
  const [searchResult, setSearchResult] = useState<SearchResultCGType | null>(null);

  const resetSearch = useCallback(() => {
    setTempSearchResult(null);
    setSearchResult(null);
  }, []);

  return {
    tempSearchResult,
    setTempSearchResult,
    searchResult,
    setSearchResult,
    resetSearch,
  };
};

const useModalState = () => {
  const [selectDayForAddDestModalVisible, setSelectDayForAddDestModalVisible] = useState(false);

  return {
    selectDayForAddDestModalVisible,
    setSelectDayForAddDestModalVisible,
  };
};

const useSelectionState = () => {
  const [selectedPoiId, setSelectedPoiId] = useState<string | null>(null);
  const [selectedDestIdList, setSelectedDestIdList] = useState<string[]>([]);

  const resetSelection = useCallback(() => {
    setSelectedDestIdList([]);
  }, []);

  return {
    selectedPoiId,
    setSelectedPoiId,
    selectedDestIdList,
    setSelectedDestIdList,
    resetSelection,
  };
};

const useAddDestination = () => {
  const [addDestinationMode, setAddDestinationMode] = useState<DestinationModeType>('normal');
  const [position, setPosition] = useState<DestinationPosition | null>(null);

  const searchState = useSearchState();
  const modalState = useModalState();
  const selectionState = useSelectionState();

  const resetAll = useCallback(() => {
    searchState.resetSearch();
    selectionState.resetSelection();
    setAddDestinationMode('normal');
    setPosition(null);
    modalState.setSelectDayForAddDestModalVisible(false);
  }, [searchState, selectionState, modalState]);

  return {
    addDestinationMode,
    setAddDestinationMode,
    position,
    setPosition,
    ...searchState,
    ...modalState,
    ...selectionState,
    resetAll,
  };
};

export default function ClientTripPage({
  initIsLoggedin,
  initTrip,
  initMemoDictionary,
  initTopSlot,
  initZzimcarProductsProps,
  isEmbedded,
  initSelectedPoiId: initSelectedPoiIdFromProps,
}: ClientTripPageProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { id: tripId } = useParams();
  const savedPois = useGetAllSavedPoisQuery();
  const [initSelectedPoiId, setInitSelectedPoiId] = useState<string | null>(initSelectedPoiIdFromProps ?? null);

  const { trip, setTrip, isLoggedin, signinResult, refetchTrip, zzimcarProductsProps } = useTripData(
    tripId as string,
    initTrip,
    initIsLoggedin,
    initZzimcarProductsProps,
  );

  const { onClickHome } = useGuestBlockBackward({
    tripId: tripId as string,
    trip,
    isLoggedin,
  });

  const { isValidCopyTrip, onClickCopyTrip } = useCopyTrip({ trip });

  const setHistoryStack = useSetAtom(historyStackAtom);
  const setTripShareModal = useSetAtom(tripShareModalAtom);
  const setGlobalTripId = useSetAtom(globalTripIdAtom);
  const setGlobalTrip = useSetAtom(globalTripAtom);
  const setTripPoiModal = useSetAtom(tripPoiModalAtom);
  const setGlobalTripDestinations = useSetAtom(globalTripDestinationsAtom);
  const setTopDestSlot = useSetAtom(topDestSlotAtom);
  const [isEditRole, setIsEditRole] = useAtom(isEditRoleAtom);
  const setConfirmModal = useSetAtom(confirmModalAtom);

  const { execute: executeAddDestinations, data: addDestinationsData } = useActionHandler({
    action: addDestinations,
    key: tripId as string,
  });

  const { execute: executeReplaceSlot, data: replaceSlotData } = useActionHandler({
    action: replaceSlot,
    key: tripId as string,
  });

  const {
    addDestinationMode,
    setAddDestinationMode,
    position: addDestinationsPosition,
    setPosition: setAddDestinationsPosition,
    tempSearchResult: selectedTempSearchResult,
    setTempSearchResult: setSelectedTempSearchResult,
    searchResult: selectedSearchResult,
    setSearchResult: setSelectedSearchResult,
    selectedPoiId,
    setSelectedPoiId,
    selectedDestIdList,
    setSelectedDestIdList,
    selectDayForAddDestModalVisible,
    setSelectDayForAddDestModalVisible,
    resetAll: resetAddDestination,
  } = useAddDestination();

  const [destinations, setDestinations] = useState<DestinationCGType[]>(
    getSortedDestinationList(filterNullUndefined(initTrip?.itinerary?.destinations)),
  );

  const [mode, setMode] = useState<TripModeType>(TRIP_MODE.VIEW);
  const [selectDayModalVisible, setSelectDayModalVisible] = useState(false);
  const [isAllThatTravelModalOpen, setIsAllThatTravelModalOpen] = useState(false);
  const [selectDayForMoveDestVisible, setSelectDayForMoveDestVisible] = useState(false);
  const [lastSearchCenterAndZoom, setLastSearchCenterAndZoom] = useState<LastSearchCenterAndZoomType>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchResults, setSearchResults] = useState<SearchResultCGType[]>([]);
  const [loadingSearchPois, setLoadingSearchPois] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [memoDictionary, setMemoDictionary] = useState<{
    [key: string]: string;
  }>(initMemoDictionary);

  const searchParams = useSearchParams();

  const scrollListToDestOnTop = useCallback(
    (destId: string, dayNumber: number, isSmooth: boolean = false, initStickyHeight?: number) => {
      const stickyHeight = (initStickyHeight ?? TRIP_CONDITIONS.IS_EDIT_MODE(mode)) ? 94 : 42;
      const $innerItinerary = document.getElementById('planner-inner-itinerary');
      const $dayContainer = document.getElementById(`dayContainer_${dayNumber}`);
      const $destCard = document.getElementById(`destCard_${destId}`);
      if (!$innerItinerary || !$dayContainer) return;

      const $dayContainerOffsetTop = $dayContainer.offsetTop;
      const $destCardOffsetTop = $destCard?.offsetTop ?? 0;

      $innerItinerary.scrollTo({
        top: $dayContainerOffsetTop - 48 - stickyHeight + $destCardOffsetTop - 10,
        behavior: isSmooth ? 'smooth' : 'instant',
      });
    },
    [mode],
  );

  const { itineraryHeightMode, setItineraryHeightMode, itineraryHeightValues } = useItineraryHeight(isEditRole);

  const onClickSearchHanlder = async (value: string) => {
    try {
      setLoadingSearchPois(true);
      setSearchResults([]);
      setSelectedSearchResult(null);
      setHasSearched(true);
      const searchResponse = await searchPoisUsingCoords(value, null, {
        latitude: centerAndZoom?.lat ?? 0,
        longitude: centerAndZoom?.lng ?? 0,
      });
      setLastSearchCenterAndZoom({
        lat: centerAndZoom?.lat ?? 0,
        lng: centerAndZoom?.lng ?? 0,
        zoom: map?.getZoom() ?? 0,
      });

      if (searchResponse) {
        setSearchResults(filterNullUndefined(searchResponse));
        setSelectedSearchResult(null);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingSearchPois(false);
    }
  };

  const { centerAndZoom, map, setTopDestInMap, SearchHelperChipsComponent, SearchRetryButtonComponent } = useMap({
    destinations: destinations,
    mode: mode,
    itineraryHeightMode: itineraryHeightMode,
    setItineraryHeightMode: setItineraryHeightMode,
    initCenter: trip?.cities?.[0]?.zone?.centerCoords ?? null,
    scrollListToDestOnTop: scrollListToDestOnTop,
    initTopSlot: initTopSlot,
    searchValue: searchValue,
    setSearchValue: setSearchValue,
    selectedSearchResult: selectedSearchResult,
    setSelectedSearchResult: setSelectedSearchResult,
    searchResults: searchResults,
    setSearchResults: setSearchResults,
    lastSearchCenterAndZoom: lastSearchCenterAndZoom,
    setLastSearchCenterAndZoom: setLastSearchCenterAndZoom,
    setLoadingSearchPois: setLoadingSearchPois,
    onClickSearch: onClickSearchHanlder,
    selectedPoiId: selectedPoiId,
  });

  const { focusedDay, topSlot, setTopSlot, onScrollHandler } = useTripScroll({
    destinations,
    mode,
    setTopDestInMap,
  });

  const { onClickPoiDetail, onClickRemoveSlot, onToggleSelectDest, onUpdateMemo } = useTripItineraryInteractions({
    tripId: tripId as string,
    mode,
    destinations,
    setDestinations,
    setSelectedDestIdList,
    setMemoDictionary,
    scrollListToDestOnTop,
  });

  const { isSortableDragging, setIsSortableDragging, onSortableDragEnd } = useDragAndDrop({
    destinations,
    tripId: tripId as string,
    setDestinations,
  });

  useEffect(() => setGlobalTrip(trip), [trip]);
  useEffect(() => setGlobalTripDestinations(destinations), [destinations]);
  useEffect(() => setGlobalTripId((tripId as string) ?? null), [tripId]);

  useEffect(() => {
    if (trip) {
      setTrip(trip);
      const newDestinations = getSortedDestinationList(filterNullUndefined(trip.itinerary?.destinations));
      if (!isEqual(newDestinations, destinations)) {
        setDestinations(newDestinations);
      }
    }
  }, [trip, destinations]);

  useEffect(() => {
    setTopDestSlot(topSlot);

    return () => {
      setTopDestSlot(null);
    };
  }, [topSlot]);

  useEffect(() => {
    const dictionary: { [key: string]: string } = {};
    destinations.forEach((_dest) => {
      if (_dest.id) {
        dictionary[_dest.id] = _dest.memo ?? '';
      }
    });
    setMemoDictionary(dictionary);
  }, [mode, destinations]);

  useEffect(() => {
    if (window.location.hash.includes('#day')) {
      const dayNumber = window.location.hash.replace('#day', '');
      if (Number.isInteger(+dayNumber)) {
        setTimeout(() => {
          scrollListToDestOnTop('', +dayNumber, false);
        }, 200);
      }
    }
  }, []);

  useEffect(() => {
    const scrollToTopOrSlot = () => {
      setTimeout(() => {
        const day1Dests =
          destinations.length > 0 &&
          destinations.filter((dest) => dest.day === 1 && !dest.accommType).sort((a, b) => a.pos - b.pos);

        if (day1Dests && day1Dests?.[0]?.id === topSlot?.id) {
          const $innerItinerary = document.getElementById('planner-inner-itinerary');
          $innerItinerary?.scrollTo({ top: 0, behavior: 'instant' });
        } else if (topSlot?.id && topSlot?.day) {
          scrollListToDestOnTop(topSlot.id, topSlot.day);
        }
      }, 100);
    };

    if (mode !== TRIP_MODE.SEARCH) {
      resetAddDestination();
      setSearchValue('');
    }
    if (mode === TRIP_MODE.SEARCH) return;
    if (mode === TRIP_MODE.EDIT) {
      setSelectedDestIdList([]);
    }
    scrollToTopOrSlot();
  }, [mode]);

  const onClickSearchPoi = useCallback(
    (searchResult: SearchResultCGType) => {
      if (searchResult.placeId === selectedSearchResult?.placeId) {
        setSelectedSearchResult(null);
        return;
      }

      if (addDestinationMode === 'selectDay') {
        setSelectDayForAddDestModalVisible(true);
        setSelectedTempSearchResult(searchResult);
      } else {
        setSelectedSearchResult(searchResult);
      }
    },
    [addDestinationsPosition, addDestinationMode, selectedSearchResult],
  );

  const { editUser, subscribeEdit, iamEdit } = useTripSocket({
    mode: mode,
    refetchTrip: refetchTrip,
    signinResult: signinResult,
    tripData: trip,
  });

  const resetSearchState = useCallback(() => {
    setAddDestinationMode('normal');
    setAddDestinationsPosition(null);
    setSelectedSearchResult(null);
    setSelectedTempSearchResult(null);
    setSearchValue('');
    setSearchResults([]);
  }, []);

  useEffect(() => {
    if (addDestinationsData && Array.isArray(addDestinationsData) && addDestinationsData.length > 0) {
      const newDests = getSortedDestinationList(filterNullUndefined(addDestinationsData));
      //  이전 데이터와 비교해서 새로운 데이터가 있는지 확인
      const hasNewDestinations = newDests.length > destinations.length;

      if (hasNewDestinations) {
        setDestinations(() => {
          const prevIdList = destinations.map((_d) => _d.id);
          const newIdList = newDests.map((_d) => _d.id).filter((_d) => !prevIdList.includes(_d));

          setTimeout(() => {
            const newId = newIdList[0];
            const newDest = newDests.find((_d) => _d.id === newId);
            if (newDest?.id && newDest.day) scrollListToDestOnTop(newDest.id, newDest.day, true, 94);
          }, 100);

          return newDests;
        });

        if (mode === TRIP_MODE.SEARCH) {
          resetSearchState();
          setMode(TRIP_MODE.VIEW);
        }
      }
    }
  }, [addDestinationsData, scrollListToDestOnTop]);

  useEffect(() => {
    if (replaceSlotData) {
      setDestinations((prev) =>
        prev.map((_d) => {
          const target = replaceSlotData?.find((_update) => _update?.id === _d.id);
          return target ?? _d;
        }),
      );
      router.back();
    }
  }, [replaceSlotData]);

  useAutofillTrip({
    tripData: trip,
    setTripData: setTrip,
    setDestinations: setDestinations,
    setTopDestInMap: setTopDestInMap,
    setTopSlot: setTopSlot,
    isLoggedin: isLoggedin,
  });

  useEffect(() => {
    const poiId = searchParams.get('poiId');

    if (poiId && initSelectedPoiId) {
      setInitSelectedPoiId(null);
      history?.replace(`/trip/${tripId}`);
      const initSelectedDestination = destinations.find((dest) => dest.poi?.id === initSelectedPoiId);
      const poiName = initSelectedDestination?.poi?.name;
      if (poiName) {
        setConfirmModal({
          visible: true,
          message: `공유된 URL에 장소 정보가 있어요!\n확인해보시겠어요?`,
          subMessage: `${poiName}`,
          onConfirm: () => {
            setConfirmModal({ visible: false });
            history?.push(`/trip/${tripId}?poiId=${initSelectedPoiId}`);
          },
          onClose: () => {
            setConfirmModal({ visible: false });
          },
          onOuterClick: () => {
            setConfirmModal({ visible: false });
          },
          confirmButtonText: '보기',
          closeButtonText: '닫기',
        });
      }
      return;
    }

    if (poiId) {
      setSelectedPoiId(poiId);

      const $plannerItinerary = document.getElementById('planner-itinerary');
      if ($plannerItinerary) {
        setTripPoiModal({
          itineraryHeightValues,
          useItineraryHeightMode: [itineraryHeightMode, setItineraryHeightMode],
        });
        $plannerItinerary.style.setProperty('opacity', '0');
      }

      return () => {
        if ($plannerItinerary) {
          $plannerItinerary.style.removeProperty('opacity');
        }
      };
    } else {
      setSelectedPoiId(null);
    }
  }, [searchParams]);

  useEffect(() => {
    const isNotHidden = document.body.style.overflowY !== 'hidden';
    if (isNotHidden) {
      document.body.style.overflowY = 'hidden';
    }

    return () => {
      if (isNotHidden) document.body.style.overflowY = 'auto';
    };
  }, []);

  useEffect(() => {
    setIsEditRole(trip?.isOwn || trip?.isCoEdit === CoEditRoleCGType.EditCGType);

    return () => {
      setIsEditRole(false);
    };
  }, [trip?.isOwn, trip?.isCoEdit]);

  useItineraryResize(itineraryHeightValues, setItineraryHeightMode);

  const {
    modifySurveyDateModalVisible,
    onClickModifySurveyDate,
    startAndEndDate,
    getDayInfo,
    setModifySurveyDateModalVisible,
  } = useTripDates(trip);

  const departurePoint = useMemo(() => {
    if (!trip?.departurePoint?.location || !trip.departurePoint.longitude || !trip.departurePoint.latitude) return null;

    return {
      location: trip.departurePoint.location,
      longitude: trip.departurePoint.longitude,
      latitude: trip.departurePoint.latitude,
    };
  }, [trip]);

  const destinationsByDay = useMemo(() => {
    const result: {
      [key: number]: {
        morningAccomm?: {
          prev: DestinationCGType | null;
          cur: DestinationCGType;
          next: DestinationCGType | null;
        };
        nightAccomm?: {
          prev: DestinationCGType | null;
          cur: DestinationCGType;
          next: DestinationCGType | null;
        };
        destinationsWithoutAccomm: {
          prev: DestinationCGType | null;
          cur: DestinationCGType;
          next: DestinationCGType | null;
        }[];
      };
    } = {};

    const fromDate = dayjs(trip?.fromDateTime);
    const toDate = dayjs(trip?.toDateTime);
    const duration = toDate.diff(fromDate, 'day') + 1;

    for (let i = 1; i <= duration; i++) {
      result[i] = {
        destinationsWithoutAccomm: [],
      };
    }

    destinations.forEach((destination, index) => {
      const day = destination.day ?? 0;
      if (!result[day]) {
        result[day] = {
          destinationsWithoutAccomm: [],
        };
      }
      const prev: DestinationCGType | null = destinations?.[index - 1] ?? null;
      const next: DestinationCGType | null = destinations?.[index + 1] ?? null;

      if (destination.accommType === 'morning') {
        result[day].morningAccomm = { prev, cur: destination, next };
      } else if (destination.accommType === 'night') {
        result[day].nightAccomm = { prev, cur: destination, next };
      } else {
        const curDestsList = result[day].destinationsWithoutAccomm;
        curDestsList.push({ prev, cur: destination, next });
      }
    });
    return result;
  }, [destinations]);

  const { title: customNameOrTitleWithCities } = useMemo(
    () => getFormattedMainCity(filterNullUndefined(trip?.cities), trip?.customName),
    [trip?.cities, trip?.customName],
  );

  const onClickChangeMode = useCallback(
    async (nextMode: TripModeType) => {
      if (!history) return;

      const tripIdPattern = /^\/trip\/[^/]+\/poi\/.*$/;
      if (tripIdPattern.test(pathname)) {
        router.back();
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      if (isEditRole && TRIP_CONDITIONS.CAN_EDIT(isEditRole, nextMode)) {
        try {
          subscribeEdit();
        } catch (error) {
          console.error(error);
        }

        history.push(`/trip/${tripId}`);

        if (nextMode === TRIP_MODE.SEARCH) {
          setAddDestinationsPosition(null);
          setAddDestinationMode('selectDay');
        }

        setMode(nextMode as TripModeType);
        setHistoryStack((prev) => [
          ...prev,
          {
            name: `${nextMode.charAt(0).toUpperCase() + nextMode.slice(1)}Mode 종료 (from client-trip-page.tsx)`,
            afterAction: () => setMode(nextMode === TRIP_MODE.EDIT ? TRIP_MODE.VIEW : mode),
          },
        ]);
      }
    },
    [subscribeEdit, pathname, isEditRole, mode, tripId],
  );

  useEffect(() => {
    const tripCustomNameEventHandler = (event: any) => {
      const customName = event?.detail?.customName ?? null;
      setTrip((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          customName: customName,
        };
      });
    };

    document.addEventListener(`trip:customName:${trip?.id}`, tripCustomNameEventHandler);
    return () => {
      document.removeEventListener(`trip:customName:${trip?.id}`, tripCustomNameEventHandler);
    };
  }, [trip?.id]);

  const onClickAddDestinations = useCallback(async () => {
    if (!tripId || addDestinationsPosition?.pos === undefined || !addDestinationsPosition.day) return;

    if (addDestinationsPosition.replace) {
      const targetDest = destinations.find((_dest) => _dest.id === addDestinationsPosition.replaceDestId);
      if (!targetDest) return;
      const slotInputs: DestinationInputCGType[] = [];
      if (targetDest.accommType === 'night' && targetDest.day) {
        const nextMorningAccomm = destinations.find((_d) => {
          return _d.accommType === 'morning' && targetDest.day && _d.day === targetDest.day + 1;
        });
        if (nextMorningAccomm && nextMorningAccomm.day) {
          slotInputs.push({
            accommType: nextMorningAccomm.accommType,
            day: nextMorningAccomm.day,
            pos: nextMorningAccomm.pos,
            memo: nextMorningAccomm.memo,
            id: nextMorningAccomm.id,
            placeId: selectedSearchResult?.placeId,
          });
        }
      } else if (targetDest.accommType === 'morning' && targetDest.day) {
        const prevNightAccomm = destinations.find((_d) => {
          return _d.accommType === 'night' && targetDest.day && _d.day === targetDest.day - 1;
        });
        if (prevNightAccomm && prevNightAccomm.day) {
          slotInputs.push({
            accommType: prevNightAccomm.accommType,
            day: prevNightAccomm.day,
            pos: prevNightAccomm.pos,
            memo: prevNightAccomm.memo,
            id: prevNightAccomm.id,
            placeId: selectedSearchResult?.placeId,
          });
        }
      }
      slotInputs.push({
        accommType: targetDest.accommType,
        day: targetDest.day,
        pos: targetDest.pos,
        memo: targetDest.memo,
        id: targetDest.id,
        placeId: selectedSearchResult?.placeId,
      });

      await executeReplaceSlot(tripId as string, slotInputs);
    } else {
      await executeAddDestinations(
        tripId as string,
        [
          {
            day: addDestinationsPosition.day,
            placeId: selectedSearchResult?.placeId,
            pos: addDestinationsPosition.pos,
            type: selectedSearchResult?.poiType,
          },
        ],
        addDestinationsPosition.pos,
        1,
      );
    }
  }, [selectedSearchResult, tripId, addDestinationsPosition, destinations, scrollListToDestOnTop]);

  const onClickReplaceDestination = useCallback(
    async (dest: DestinationCGType) => {
      let defaultInputValue = PoiType.attraction;
      if (dest.slotType) {
        defaultInputValue = mapSlotTypeToPoiType(dest.slotType);
      }
      if (!dest.day || !dest.id) return;
      await onClickChangeMode(TRIP_MODE.SEARCH);
      setSearchValue(defaultInputValue);
      setAddDestinationMode('normal');
      setAddDestinationsPosition({
        day: dest.day,
        pos: dest.pos,
        replace: true,
        replaceDestId: dest.id,
      });
    },
    [onClickChangeMode, subscribeEdit, editUser],
  );

  useEffect(() => {
    const onClickReplaceDestinationHandler = (event: any) => {
      if (event.detail.destination) {
        onClickReplaceDestination(event.detail.destination);
      }
    };

    document.addEventListener('onClickReplaceDestination', onClickReplaceDestinationHandler);
    return () => {
      document.removeEventListener('onClickReplaceDestination', onClickReplaceDestinationHandler);
    };
  }, [onClickAddDestinations]);

  const closePoiModal = useCallback(() => {
    if (searchParams.get('poiId')) {
      router.back();
    }
  }, [searchParams]);

  const onChangeSearchValue = useCallback((value: string) => {
    setSearchValue(value);
    setHasSearched(false);
  }, []);

  useEffect(() => {
    if (!trip) return;
    const isAllThatTravel = window.sessionStorage.getItem('allthattravel_survey');
    if (!trip.autofillAvailable && isAllThatTravel) {
      setIsAllThatTravelModalOpen(true);
    }
    if (!trip.autofillAvailable && !isAllThatTravel) {
      setIsAllThatTravelModalOpen(false);
    }
  }, [pathname]);

  const onClickSearchHandler = useCallback(
    async (value: string) => {
      try {
        setLoadingSearchPois(true);
        setSearchResults([]);
        setSelectedSearchResult(null);
        setHasSearched(true);
        const searchResponse = await searchPoisUsingCoords(value, null, {
          latitude: centerAndZoom?.lat ?? 0,
          longitude: centerAndZoom?.lng ?? 0,
        });
        setLastSearchCenterAndZoom({
          lat: centerAndZoom?.lat ?? 0,
          lng: centerAndZoom?.lng ?? 0,
          zoom: map?.getZoom() ?? 0,
        });

        if (searchResponse) {
          setSearchResults(filterNullUndefined(searchResponse));
          setSelectedSearchResult(null);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingSearchPois(false);
      }
    },
    [centerAndZoom, map],
  );

  const onClearSearch = useCallback(() => {
    setHasSearched(false);
    setSearchResults([]);
    setSelectedSearchResult(null);
  }, []);

  const openTripShareModal = useCallback(async () => {
    const currentTripData = await getTrip(tripId as string);
    if (!currentTripData.data) return;

    if (currentTripData.data.isOwn === initTrip?.isOwn && currentTripData.data.isCoEdit === initTrip?.isCoEdit) {
      setHistoryStack((prev) => {
        if (prev.length > 0) {
          return [...prev, {}];
        }
        return prev;
      });
      setTripShareModal({
        tripName: customNameOrTitleWithCities,
        isOwn: trip?.isOwn ?? false,
      });
      router.push(`/trip/share/${tripId}`, { scroll: false });
    } else {
      setConfirmModal({
        visible: true,
        message: '일정표 공유 권한이 없어요.\n로그인 후 다시 시도해주세요.',
        onConfirm: () => {
          redirectToZzimSignin();
        },
        onClose: () => {
          setConfirmModal({ visible: false });
        },
      });
    }
  }, [initTrip]);

  const 실험실짱 = useMemo(() => {
    return searchParams.get('실험실') === '짱';
  }, [searchParams]);

  return (
    <>
      <div className='w-full loaded-wrap'>
        <TripTopBar
          title={TRIP_CONDITIONS.IS_SEARCH_MODE(mode) ? '여행지 검색' : customNameOrTitleWithCities}
          isEmbedded={isEmbedded}
          trip={trip}
          onClickHome={onClickHome}
        />

        <TripMapView
          SearchHelperChipsComponent={SearchHelperChipsComponent}
          SearchRetryButtonComponent={SearchRetryButtonComponent}
          isEditRole={isEditRole}
          mode={mode}
          editUser={editUser}
          onClickChangeMode={onClickChangeMode}
        >
          {TRIP_CONDITIONS.IS_SEARCH_MODE(mode) && isEditRole && (
            <TripSearchBar
              searchValue={searchValue}
              onChangeSearchValue={onChangeSearchValue}
              onClickSearch={onClickSearchHandler}
              onClearSearch={onClearSearch}
              onSearchInteraction={closePoiModal}
            />
          )}
        </TripMapView>

        <div
          id='planner-itinerary'
          className='fixed bottom-0 w-full max-w-480pxr overflow-hidden rounded-t-16pxr flex flex-col z-10 bg-white'
          style={{
            height: itineraryHeightValues.default,
          }}
        >
          <button id='itineraryHandler' className='flex justify-center w-full bg-white pt-12pxr pb-32pxr'>
            <span className='w-45pxr h-4pxr rounded-full bg-[#D9D9D9] block'></span>
          </button>

          <div
            id='planner-inner-itinerary'
            className='overflow-x-hidden overflow-y-scroll bg-white'
            style={{ paddingBottom: itineraryHeightValues.default }}
            onScroll={onScrollHandler}
          >
            {TRIP_CONDITIONS.SHOULD_SHOW_LOGIN_PROMPT(mode, trip?.isOwn ?? false, isLoggedin) && (
              <TripGuestLoginPrompt tripId={tripId as string} />
            )}
            {TRIP_CONDITIONS.IS_NOT_SEARCH_MODE(mode) && !isEmbedded && (
              <>
                {!isAllThatTravelModalOpen && (
                  <TripHeader
                    isEditRole={isEditRole}
                    startAndEndDate={startAndEndDate}
                    onClickModifySurveyDate={onClickModifySurveyDate}
                    onClickTripShare={openTripShareModal}
                  />
                )}
                {isAllThatTravelModalOpen && (
                  <div className='w-full px-20pxr relative pt-20pxr'>
                    <button
                      id='allthattravel-mode-trip-share-button'
                      className='w-full h-50pxr bg-new-point-500 rounded-8pxr body-16-md text-white'
                      style={{
                        boxShadow: '0px 0px 20.6px 0px rgba(49, 119, 255, 0.38)',
                      }}
                      onClick={openTripShareModal}
                    >
                      일정표 공유하고 탈출코드 입력하기
                    </button>
                    <span
                      id='allthattravel-mode-trip-share-button-hand-cursor'
                      className='absolute -bottom-13pxr right-35pxr'
                    >
                      <HandCursor2734 />
                    </span>
                  </div>
                )}
              </>
            )}
            {trip && !isEmbedded && (
              <div className={`${TRIP_CONDITIONS.IS_VIEW_MODE(mode) ? 'block' : 'hidden'}`}>
                <ZzimcarProductsList zzimcarProductsProps={zzimcarProductsProps} surveyId={trip.id} />
              </div>
            )}
            {TRIP_CONDITIONS.IS_NOT_SEARCH_MODE(mode) && departurePoint && (
              <div className={`relative p-20pxr`}>
                <div
                  className='relative flex items-center bg-white border border-gray-300 py-16pxr px-16pxr gap-8pxr rounded-8pxr z-[1]'
                  style={{
                    boxShadow: '0px 4px 10px 0px rgba(220, 228, 232, 0.50)',
                  }}
                >
                  <span className='flex-none text-gray-600 body-12-r'>출발 위치</span>
                  <span className='text-gray-800 sub-heading-16-m'>{departurePoint.location}</span>
                </div>
                {TRIP_CONDITIONS.IS_VIEW_MODE(mode) && (
                  <div className='absolute top-0 h-full bg-gray-300 w-1pxr left-42pxr'></div>
                )}
              </div>
            )}
            {TRIP_CONDITIONS.IS_EDIT_MODE(mode) && (
              <div className='sticky top-0 bg-white px-20pxr pb-20pxr z-[2]'>
                <div className='text-center h-36pxr py-9pxr px-12pxr rounded-4pxr bg-point-100 text-info sub-heading-14-m'>
                  수정한 일정표 내용은 자동으로 저장 돼요.
                </div>
              </div>
            )}

            {TRIP_CONDITIONS.IS_NOT_SEARCH_MODE(mode) && (
              <TripItineraryView
                destinationsByDay={destinationsByDay}
                destinations={destinations}
                focusedDay={focusedDay}
                memoDictionary={memoDictionary}
                selectedDestIdList={selectedDestIdList}
                isSortableDragging={isSortableDragging}
                mode={mode}
                isEmbedded={isEmbedded}
                getDayInfo={getDayInfo}
                setDestinations={setDestinations}
                onSortableDragEnd={onSortableDragEnd}
                setIsSortableDragging={setIsSortableDragging}
                onClickPoiDetail={onClickPoiDetail}
                onClickChangeMode={onClickChangeMode}
                setAddDestinationMode={setAddDestinationMode}
                setAddDestinationsPosition={setAddDestinationsPosition}
                onClickRemoveSlot={onClickRemoveSlot}
                onToggleSelectDest={onToggleSelectDest}
                onUpdateMemo={onUpdateMemo}
                setSelectDayModalVisible={setSelectDayModalVisible}
                실험실짱={실험실짱}
              />
            )}

            {TRIP_CONDITIONS.IS_SEARCH_MODE(mode) && (
              <TripSearchView
                searchValue={searchValue}
                loadingSearchPois={loadingSearchPois}
                hasSearched={hasSearched}
                searchResults={searchResults}
                selectedSearchResult={selectedSearchResult}
                savedPois={savedPois ?? []}
                tripId={tripId as string}
                onClickSearchPoi={onClickSearchPoi}
                onClickAddDestinations={onClickAddDestinations}
                addDestinationsPosition={addDestinationsPosition}
                isEditRole={isEditRole}
                itineraryHeightMode={itineraryHeightMode}
                setHistoryStack={setHistoryStack}
                history={history}
                tripCities={filterNullUndefined(trip?.cities ?? [])}
              />
            )}
          </div>
        </div>

        {trip && !isEmbedded && (
          <>
            <SelectDayModal
              isVisible={selectDayModalVisible}
              focusedDay={focusedDay}
              scrollListToDestOnTop={scrollListToDestOnTop}
              trip={trip}
            />
            <SelectDayForAddDestModal
              useIsVisible={[selectDayForAddDestModalVisible, setSelectDayForAddDestModalVisible]}
              selectedTempSearchResult={selectedTempSearchResult}
              selectedSearchResult={selectedSearchResult}
              setSelectedSearchResult={setSelectedSearchResult}
              setAddDestinationsPosition={setAddDestinationsPosition}
              destinations={destinations}
              addDestinationsPosition={addDestinationsPosition}
              trip={trip}
              onClickAddDestinations={onClickAddDestinations}
            />
            <ModifySurveyDateModal
              isVisible={modifySurveyDateModalVisible}
              setIsVisible={setModifySurveyDateModalVisible}
              trip={trip}
              editUser={editUser}
              iamEdit={iamEdit}
            />
            <SelectDayForMoveDestModal
              trip={trip}
              isVisible={selectDayForMoveDestVisible}
              destinations={destinations}
              setDestinations={setDestinations}
              selectedDestIdList={selectedDestIdList}
              setSelectedDestIdList={(destIds) => setSelectedDestIdList(destIds)}
              scrollListToDestOnTop={scrollListToDestOnTop}
            />
          </>
        )}
        <TripBottomBar
          mode={mode}
          trip={trip}
          isEmbedded={isEmbedded}
          isValidCopyTrip={isValidCopyTrip}
          itineraryHeightMode={itineraryHeightMode}
          selectedDestIdList={selectedDestIdList}
          destinations={destinations}
          memoDictionary={memoDictionary}
          setDestinations={setDestinations}
          onClickCopyTrip={onClickCopyTrip}
          onClickReplaceDestination={onClickReplaceDestination}
          setSelectDayForMoveDestVisible={setSelectDayForMoveDestVisible}
          tripId={tripId as string}
          customNameOrTitleWithCities={customNameOrTitleWithCities}
        />
      </div>
      <PoiModalInTrip
        selectedPoiId={selectedPoiId}
        itineraryHeightValues={itineraryHeightValues}
        useItineraryHeightMode={[itineraryHeightMode, setItineraryHeightMode]}
        poiModalInTripProps={{
          isEditRole: isEditRole,
          iamEdit: iamEdit,
          editUser: editUser,
          tripId: tripId as string,
          tripDestinations: destinations,
          setTripDestinations: setDestinations,
          scrollListToDestOnTop: scrollListToDestOnTop,
        }}
        isEmbedded={isEmbedded}
      />
      {isEmbedded && <EmbeddedOverlay />}
      {isAllThatTravelModalOpen && <AllThatTravelHintModal step={4} />}
    </>
  );
}
